import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { validateAb1File, getBase64 } from '../../utils/ab1FileUtils';
import { analyze } from '../../utils/api';
import validationSchema, { TALEN_ZFN_MODES } from './validationSchema';

import './InputForm.css';

function InputForm({ onAnalysisComplete, onStatusUpdate, onErrorsUpdate, onProcessingChange }) {
    // react-hook-form の初期化
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState,
        clearErrors,
        trigger
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: '',
            mode: 'Cas9',
            // Cas9 / Cas12a 専用
            targetSiteList: '',
            pam: 'NGG',
            // Substitution detection is opt-in because substitution proposals
            // can absorb WT coefficients when indel quantification is primary.
            substitutionFivePrimeMaxSize: 0,
            substitutionThreePrimeMaxSize: 0,
            deletionMaxSize: 50,
            insertionMaxSize: 3,
            // TALEN / ZFN 専用
            leftBindingSite: '',
            rightBindingSite: '',
            spacer: 15,                    // Spacer 長スライダー（TALEN デフォルト 15 bp）
            deletionFivePrimeMaxSize: 5,
            deletionThreePrimeMaxSize: 5,
            // 共通
            abiControlFile: null,
            abiEditedFile: null,
            enabledLasso: false,
            variableSelection: 'all',
        },
    });

    // 各ファイルのバリデーションメッセージ
    const [controlFileMessage, setControlFileMessage] = useState('');
    const [editedFileMessage, setEditedFileMessage] = useState('');

    // AB1ファイルのバリデーション結果
    const [controlFileContentValid, setControlFileContentValid] = useState(false);
    const [editedFileContentValid, setEditedFileContentValid] = useState(false);

    // 二重クリック防止用の時間管理
    const [lastClickTime, setLastClickTime] = useState(0);

    // 現在選択されているモードを監視
    const currentMode = watch('mode');
    const isTalenZfnMode = TALEN_ZFN_MODES.includes(currentMode);

    // モードが変わったら Spacer スライダーのデフォルト値を変更する（§6.4）
    // TALEN: 15 bp, ZFN: 9 bp
    useEffect(() => {
        if (currentMode === 'TALEN') {
            setValue('spacer', 15);
        } else if (currentMode === 'ZFN') {
            setValue('spacer', 9);
        }
    }, [currentMode, setValue]);

    /**
     * ファイル変更イベントハンドラ
     * 
     * ファイルオブジェクトの保持、ファイル項目のメッセージとコンテンツの正常異常のセットを同時に行います。
     * 
     * @param {React.ChangeEvent<HTMLInputElement>} e ファイル項目のチェンジイベント
     * @param {Function} fieldName validationSchemaにあるab1ファイル項目名
     * @param {Function} setMessage ファイル項目のメッセージのセッター
     * @param {Function} setContentValid ファイルコンテンツのバリデーション結果のセッター
     */
    const handleFileChange = async (e, fieldName, setMessage, setContentValid) => {
        const file = e.target.files[0];

        setValue(fieldName, file);
        clearErrors(fieldName);

        const { isValid, msg } = await validateAb1File(file);
        
        setMessage(msg);
        setContentValid(isValid);
        
        await trigger(fieldName); 
    };

    /**
     * フォーム送信イベントハンドラ
     */
    const onSubmit = handleSubmit(async (data) => {
        // 二重クリック防止(10秒以内)
        const currentTime = Date.now() / 1000;
        if (currentTime - lastClickTime < 10) {
            onStatusUpdate('warning', "Please do not click multiple times and wait 10 seconds before clicking again.");
            return;
        }
        setLastClickTime(currentTime);

        onStatusUpdate('info', "Processing...");
        onErrorsUpdate([]);
        onAnalysisComplete(null);

        onProcessingChange(true);

        try {
            onStatusUpdate('info', "Encoding files and sending analysis request...");

            const abiControlBase64 = await getBase64(data.abiControlFile);
            const abiEditedBase64 = await getBase64(data.abiEditedFile);

            const isTalen = TALEN_ZFN_MODES.includes(data.mode);

            const payload = isTalen
                ? {
                    name: data.name,
                    mode: data.mode,
                    abi_control_file_base64: abiControlBase64,
                    abi_edited_file_base64: abiEditedBase64,
                    left_binding_site: data.leftBindingSite,
                    right_binding_site: data.rightBindingSite,
                    spacer: data.spacer,
                    deletion_five_prime_max_size: data.deletionFivePrimeMaxSize,
                    deletion_three_prime_max_size: data.deletionThreePrimeMaxSize,
                    enabled_lasso: data.enabledLasso,
                    variable_selection: data.variableSelection,
                    ice: false,
                }
                : {
                    name: data.name,
                    mode: data.mode,
                    target_site_list: data.targetSiteList.split(' ').filter(s => s),
                    abi_control_file_base64: abiControlBase64,
                    abi_edited_file_base64: abiEditedBase64,
                    pam: data.pam,
                    substitution_five_prime_max_size: data.substitutionFivePrimeMaxSize,
                    substitution_three_prime_max_size: data.substitutionThreePrimeMaxSize,
                    deletion_max_size: data.deletionMaxSize,
                    insertion_max_size: data.insertionMaxSize,
                    enabled_lasso: data.enabledLasso,
                    variable_selection: data.variableSelection,
                    ice: false,
                };

            const response = await analyze(payload);

            if (response.output_directory && response.indel_json && response.all_json && response.contribs_json) {
                onStatusUpdate('success', "Analysis Complete!!!");
                onAnalysisComplete({
                    output_directory: response.output_directory,
                    indel_json: response.indel_json,
                    all_json: response.all_json,
                    contribs_json: response.contribs_json,
                    discord_plot_base64: response.discord_plot_base64,
                    editing_outcomes_plot_base64: response.editing_outcomes_plot_base64
                });
            } else {
                onStatusUpdate('error', "ERROR... Analysis response was incomplete. Please check your input and try again.");
                onAnalysisComplete(null);
            }

        } catch (error) {
            console.error("Analysis process failed:", error);

            let displayMessage = `ERROR... ${error.message || "An unexpected error occurred. Please refresh the page and try again."}`;
            let displayErrors = [];

            if (error.backendMessage) {
                displayMessage = `ERROR... ${error.backendMessage}`;
                if (Array.isArray(error.backendErrors) && error.backendErrors.length > 0) {
                    displayErrors = error.backendErrors;
                }
            }
            
            onStatusUpdate('error', displayMessage);
            // 現状では複数のエラーを受信することはないが、 onErrorsUpdate での複数エラー表示に対応
            if (displayErrors.length > 0) {
                onErrorsUpdate(displayErrors);
            }
            onAnalysisComplete(null);

        } finally {
            onProcessingChange(false);
        }
    });

    const canAnalyze = formState.isValid && !formState.isSubmitting; 

    return (
        <form onSubmit={onSubmit} data-testid="input-form">
            <h3>Citation information</h3>
            <p>Nakamae, K., Ide, S., Ohnuki, N. *et al*. <a href="https://doi.org/10.1186/s12859-025-06139-8">PtWAVE: a high-sensitive deconvolution software of sequencing trace for the detection of large indels in genome editing.</a> BMC Bioinformatics **26**, 114 (2025). doi: 10.1186/s12859-025-06139-8.</p>
            <h3>Input parameters</h3>
            <p>📖 Download Example Files <a href="https://doi.org/10.6084/m9.figshare.28899584.v1">HERE</a>.</p>

            <div className="form-columns">
                <div className="form-column">
                    <label>
                        Name:
                        <input type="text" {...register('name')} />
                        {formState.errors.name && <p className="error-msg">{formState.errors.name.message}</p>}
                    </label>
                    <label>
                        Tools:
                        <select {...register('mode')}>
                            <option value="Cas9">Cas9</option>
                            <option value="TALEN">TALEN</option>
                            <option value="ZFN">ZFN</option>
                        </select>
                        {formState.errors.mode && <p className="error-msg">{formState.errors.mode.message}</p>}
                    </label>

                    {/* Cas9 / Cas12a 専用フィールド */}
                    {!isTalenZfnMode && (
                        <label>
                            Protospacer sequence:
                            <textarea {...register('targetSiteList')} placeholder="Enter sequences separated by space"></textarea>
                            {formState.errors.targetSiteList && <p className="error-msg">{formState.errors.targetSiteList.message}</p>}
                        </label>
                    )}

                    {/* TALEN / ZFN 専用フィールド */}
                    {isTalenZfnMode && (
                        <>
                            <label>
                                {currentMode === 'TALEN'
                                    ? 'Left TALEN binding site (plus strand):'
                                    : 'Left ZF array binding site (plus strand):'}
                                <input type="text" {...register('leftBindingSite')} placeholder="Enter left binding site sequence" />
                                {formState.errors.leftBindingSite && <p className="error-msg">{formState.errors.leftBindingSite.message}</p>}
                            </label>
                            <label>
                                {currentMode === 'TALEN'
                                    ? 'Right TALEN binding site (plus strand):'
                                    : 'Right ZF array binding site (plus strand):'}
                                <input type="text" {...register('rightBindingSite')} placeholder="Enter right binding site sequence" />
                                {formState.errors.rightBindingSite && <p className="error-msg">{formState.errors.rightBindingSite.message}</p>}
                            </label>
                        </>
                    )}

                    <label>
                        Upload ab1 files for WT sample:
                        <input type="file" accept=".ab1" onChange={(e) => handleFileChange(e, 'abiControlFile', setControlFileMessage, setControlFileContentValid)} />
                        {controlFileMessage && <p className={controlFileContentValid ? 'success-msg' : 'error-msg'}>{controlFileMessage}</p>}
                        {formState.errors.abiControlFile && <p className="error-msg">{formState.errors.abiControlFile.message}</p>}
                    </label>
                    <label>
                        Upload ab1 files for Edited sample:
                        <input type="file" accept=".ab1" onChange={(e) => handleFileChange(e, 'abiEditedFile', setEditedFileMessage, setEditedFileContentValid)} />
                        {editedFileMessage && <p className={editedFileContentValid ? 'success-msg' : 'error-msg'}>{editedFileMessage}</p>}
                        {formState.errors.abiEditedFile && <p className="error-msg">{formState.errors.abiEditedFile.message}</p>}
                    </label>
                </div>
                <div className="form-column">
                    {/* Cas9 / Cas12a 専用フィールド */}
                    {!isTalenZfnMode && (
                        <>
                            <label>
                                PAM sequence:
                                <input type="text" {...register('pam')} />
                                {formState.errors.pam && <p className="error-msg">{formState.errors.pam.message}</p>}
                            </label>
                            <label>
                                Substitution range of PAM-distal region: {watch('substitutionFivePrimeMaxSize')}
                                <input type="range" min="0" max="3" {...register('substitutionFivePrimeMaxSize', { valueAsNumber: true })} />
                            </label>
                            <label>
                                Substitution range of PAM-proximal region: {watch('substitutionThreePrimeMaxSize')}
                                <input type="range" min="0" max="3" {...register('substitutionThreePrimeMaxSize', { valueAsNumber: true })} />
                            </label>
                            <label>
                                Maximum deletion range: {watch('deletionMaxSize')}
                                <input type="range" min="0" max="100" {...register('deletionMaxSize', { valueAsNumber: true })} />
                            </label>
                            <label>
                                Maximum insertion range: {watch('insertionMaxSize')}
                                <input type="range" min="0" max="10" {...register('insertionMaxSize', { valueAsNumber: true })} />
                            </label>
                        </>
                    )}

                    {/* TALEN / ZFN 専用フィールド */}
                    {isTalenZfnMode && (
                        <>
                            <label>
                                Spacer: {watch('spacer')}
                                <input type="range" min="1" max="30" {...register('spacer', { valueAsNumber: true })} />
                                {formState.errors.spacer && <p className="error-msg">{formState.errors.spacer.message}</p>}
                            </label>
                            <label>
                                Maximum deletion size from 5prime-end flanking region: {watch('deletionFivePrimeMaxSize')}
                                <input type="range" min="0" max="15" {...register('deletionFivePrimeMaxSize', { valueAsNumber: true })} />
                            </label>
                            <label>
                                Maximum deletion size from 3prime-end flanking region: {watch('deletionThreePrimeMaxSize')}
                                <input type="range" min="0" max="15" {...register('deletionThreePrimeMaxSize', { valueAsNumber: true })} />
                            </label>
                        </>
                    )}

                    <label>
                        <input type="checkbox" {...register('enabledLasso')} />
                        LASSO model
                    </label>
                    <label>
                        Modes of variable selection:
                        <select {...register('variableSelection')}>
                            <option value="all">all</option>
                            <option value="random">random</option>
                            <option value="backstep">backstep</option>
                        </select>
                        {formState.errors.variableSelection && <p className="error-msg">{formState.errors.variableSelection.message}</p>}
                    </label>
                </div>
            </div>

            <button type="submit" disabled={!canAnalyze}>
                {formState.isSubmitting ? 'Analyzing...' : 'Analyze'}
            </button>
        </form>
    );
}

export default InputForm;
